/** @module csi/widget/DateRangeCalendar */
define([
	"dojo/_base/declare",
	"dojo/_base/lang",
	"dojo/date",
	"dojo/dom-class",
	"dijit/Calendar",
	"dijit/focus",
	"csi/_base/underscore"
], function(declare,lang,date,domClass,Calendar,focus,_) {

	return declare(Calendar, {
		/** @property {Date} [endDate] - end date of date range */
		endDate: null,

		/** @property {boolean} resetRange - if true, then next day click will reset start and end dates; otherwise click will expand range from single day to range */
		resetRange: true,

		/** @property {Date} [startDate] - start date of date range */
		startDate: null,

		/** @property {boolean} _bRedraw - used */
		_bForceRedraw: true,

		/** @property {DOMNode[]} _rangeCells - list of date cells in range */
		_rangeCells: null,

		/** @property {boolean} _trackCurrentFocus - transient setting used to make date range track change to current focus */
		_trackCurrentFocus: false,

		/** @property {boolean} _trackedCurrentFocus - indicates if previous change also tracked the current focus */
		_trackedCurrentFocus: false,

		/**
		 * Extension point called when start and end dates change
		 * @param {Date} start
		 * @param {Date} end
		 */
		onChangeDates: function(start, end) { },

		/**
		 * Set up listeners for changes to date range
		 */
		postCreate: function() {
			var self = this;

			function _redraw() {
				self._markSelectedDates([]);
			}

			this.inherited(arguments);

			this.dateModule = lang.delegate(this.dateModule, {
				difference: function(date1, date2, interval) {
					// this function is only used to determine if calendar needs to switch months when currentFocus changes; the goal is to prevent switching when
					// day is near start of end of the month
					console.assert(interval === "month");

					// if day not currently shown, then must switch months; also force redraw first time to make sure dates are disabled since it is first
					// populated before the isDisabledDate function is applied
					if (self._bForceRedraw || !self._getNodeByDate(date2)) {
						self._bForceRedraw = false;
						return 1;
					}

					// if month is not the same, and neither is start or end date (date2 should be one of them), then switch months
					if (self._trackedCurrentFocus) {
						var diff = date.difference(date1, date2, interval);
						var diffStart = date.difference(date1, self.startDate, interval);
						var diffEnd = date.difference(date1, self.endDate, interval);

						return diff && diffStart && diffEnd ? 1 : 0;
					}

					return date.difference(date1, date2, interval);
				}
			});

			this.watch("currentFocus", function(attr, oldValue, newValue) {
				self._onChangeCurrentFocus(newValue);
			});

			this.watch("endDate", _redraw);
			this.watch("startDate", _redraw);
		},

		/**
		 * Return first date in current calendar view
		 * @return {Date}
		 */
		getFirstDate: function() {
			// NOTE: be careful not to use tbody.firstChild as it is text
			var tbody = this.dateRowsNode;
			var td = tbody.children[0].children[0];
			return new Date(td.dijitDateValue);
		},

		/**
		 * Disable all keyboard controls on calendar
		 * @param {Event} evt
		 * @return {boolean}
		 */
		handleKey: function(evt) {
			return false;
		},

		/**
		 * New start and end date have been set, so now update calendar to best show date range and not switch months if it can be avoided.
		 * @param {Date} newStartDate - recently changed start date, if any
		 * @param {Date} newEndDate - recently changed end date, if any
		 * @param {boolean} bForceRedraw - force redrawing of calendar; necessary when settings change, such isDisabledDate function
		 */
		updateRange: function(newStartDate, newEndDate, bForceRedraw) {
			// if calendar is being controlled from outside, then reset resetRange property so next click will start over rather than
			// try to extend date range set from outside
			this.set("resetRange", true);

			var focusDate = newStartDate || newEndDate;
			if (!focusDate) {
				return;
			}

			// normally set focus based on what last changed, but need to handle edge case that occurs when switching from Past Month to Past Week -
			// it is possible that switching to new month will show full range, but since start date is in current month it does not show full range
			var startNode = this._getNodeByDate(this.startDate);
			var endNode = this._getNodeByDate(this.endDate);

			if (startNode && !endNode && (this.startDate.getMonth() != this.endDate.getMonth())) {
				var endOfWeekNode = startNode.parentNode.lastChild;
				var endOfWeek = this._patchDate(endOfWeekNode.dijitDateValue);
				if (endOfWeek.getMonth() == this.endDate.getMonth()) {
					focusDate = this.endDate;
				}
			}

			this._trackedCurrentFocus = true;

			if (bForceRedraw || (this.currentFocus.getTime() != focusDate.getTime())) {
				this._bForceRedraw = bForceRedraw;
				this._setCurrentFocusAttr(focusDate);

				// in some cases, we prevent the calendar from switching months even though the current focus has changed; in such cases,
				// even if we don't change months, we still need to update selections
				this._markSelectedDates([]);
			}
		},

		/**
		 * Style cells in range
		 * @param {Date[]} dates
		 */
		_markSelectedDates: function(dates) {
			var cssBoundary = "csi-date-range-boundary";
			var cssRange = "csi-date-range-selected";
			var cells = this._rangeCells;
			var start = this.startDate;
			var end = this.endDate;

			// clear previous styling
			_.each(cells, function(cell) {
				domClass.remove(cell, cssBoundary);
				domClass.remove(cell, cssRange);
			});

			// add new styling for range
			this._rangeCells = cells = [];

			if (start && end) {
				var dt = this._patchDate(start);
				var endTime = end.getTime();
				var cell;

				while (dt.getTime() <= endTime) {
					if (cell = this._getNodeByDate(dt)) {
						// highlight cell in range
						domClass.add(cell, cssRange);
						cells.push(cell);
					} else if (cells.length) {
						// break out of loop once a date is off current calendar
						break;
					}

					dt.setDate(dt.getDate() + 1);
				}

				// mark start and end dates as boundaries
				if (cell = this._getNodeByDate(start)) {
					domClass.add(cell, cssBoundary);
					cells.push(cell);
				}

				if (cell = this._getNodeByDate(end)) {
					domClass.add(cell, cssBoundary);
					cells.push(cell);
				}
			}

			// ignore value of widget, and only select the currentFocus
			this.inherited(arguments, [this.currentFocus ? [this.currentFocus] : []]);
		},

		/**
		 * Update start and end dates when focused date changes if currently tracking focus
		 * @param {Date} value
		 */
		_onChangeCurrentFocus: function(value) {
			if (!this._trackCurrentFocus) {
				this._trackedCurrentFocus = false;
				return;
			}

			var newTime = value.getTime();
			var start = this.startDate;
			var end = this.endDate;

			if (!this.isDisabledDate(value)) {
				var resetRange = this.resetRange;

				if (!start || !end) {
					resetRange = true;
				}

				if (resetRange) {
					start = end = value;
				} else if (newTime < start.getTime()) {
					end = start;
					start = value;
				} else {
					end = value;
				}

				this.set("startDate", start);
				this.set("endDate", end);

				// notify others of change - it is done together in order to preserve time range when possible
				this.onChangeDates(start, end);

				this.set("resetRange", !resetRange);
			}

			this._trackCurrentFocus = false;
			this._trackedCurrentFocus = true;
		},

		/**
		 * Indicate that month change should be optimized when changing date using keyboard.
		 * @param {Event} evt
		 * @return {boolean}
		 */
		_onDayClick: function(evt) {
			this._trackCurrentFocus = true;
			this.inherited(arguments);
		},

		/**
		 * Set appropriate hours on new date
		 * @param {Date} value
		 */
		_setEndDateAttr: function(value) {
			this._set("endDate", value ? this._patchDate(value) : value);
		},

		/**
		 * Set appropriate hours on new date
		 * @param {Date} value
		 */
		_setStartDateAttr: function(value) {
			this._set("startDate", value ? this._patchDate(value) : value);
		}
	});
});